import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Bell } from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { listMyNotifications, markNotificationRead } from "@/lib/notifications.functions";
import { useAuth } from "@/lib/use-auth";

export function NotificationsBell() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const listFn = useServerFn(listMyNotifications);
  const markFn = useServerFn(markNotificationRead);

  const q = useQuery({
    enabled: !!user,
    queryKey: ["my-notifications"],
    queryFn: () => listFn(),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const items = q.data ?? [];
  const unread = items.filter((n) => !n.read_at).length;

  const markAll = useMutation({
    mutationFn: () => markFn({ data: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-notifications"] }),
  });

  useEffect(() => {
    if (!user) qc.removeQueries({ queryKey: ["my-notifications"] });
  }, [user, qc]);

  if (!user) return null;

  return (
    <DropdownMenu onOpenChange={(o) => o && unread > 0 && markAll.mutate()}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute right-1 top-1 grid h-4 min-w-4 place-items-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="border-b border-border p-3">
          <p className="font-display text-base">Notifiche</p>
          <p className="text-xs text-muted-foreground">{unread} non lette</p>
        </div>
        <ul className="max-h-96 divide-y divide-border overflow-y-auto">
          {items.length === 0 && (
            <li className="p-6 text-center text-xs text-muted-foreground">Nessuna notifica</li>
          )}
          {items.map((n) => {
            const body = (
              <div className={`flex flex-col gap-1 p-3 transition hover:bg-secondary/60 ${!n.read_at ? "bg-gold/5" : ""}`}>
                <p className="text-sm font-medium">{n.title}</p>
                {n.body && <p className="text-xs text-muted-foreground">{n.body}</p>}
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {new Date(n.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            );
            return (
              <li key={n.id}>
                {n.link ? <Link to={n.link as never}>{body}</Link> : body}
              </li>
            );
          })}
        </ul>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}